import api from './api';

const WEB_PUSH_SERVICE_WORKER_URL = '/contact-tel-web-push-sw.js';

export type BrowserPushPermission = NotificationPermission | 'unsupported';

export interface BrowserPushStatus {
  enabled: boolean;
  configured: boolean;
  publicKey: string;
  subscribed: boolean;
  subscriptionCount: number;
}

type BrowserPushSubscribeResponse = BrowserPushStatus;

function isBrowserPushApiAvailable(): boolean {
  return (
    typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
  );
}

export function getBrowserPushPermission(): BrowserPushPermission {
  if (!isBrowserPushApiAvailable()) {
    return 'unsupported';
  }
  return Notification.permission;
}

export function isWebPushSupported(): boolean {
  return isBrowserPushApiAvailable();
}

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputBuffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(outputBuffer);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputBuffer;
}

async function registerWebPushServiceWorker(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(WEB_PUSH_SERVICE_WORKER_URL, {
    scope: '/',
    updateViaCache: 'none',
  });
  registration.update().catch(() => undefined);
  return registration;
}

function serializeSubscription(subscription: PushSubscription, languageCode?: string) {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
    languageCode: languageCode || navigator.language || '',
  };
}

export async function fetchWebPushStatus(): Promise<BrowserPushStatus> {
  const response = await api.get('/api/notifications/push/status/');
  return response.data as BrowserPushStatus;
}

export async function subscribeToWebPush(languageCode?: string): Promise<BrowserPushSubscribeResponse> {
  if (!isBrowserPushApiAvailable()) {
    throw new Error('web_push_unsupported');
  }

  const status = await fetchWebPushStatus();
  if (!status.enabled || !status.configured || !status.publicKey) {
    throw new Error('web_push_unavailable');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied' ? 'web_push_denied' : 'web_push_dismissed');
  }

  const registration = await registerWebPushServiceWorker();
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription = existingSubscription || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToArrayBuffer(status.publicKey),
  });

  const response = await api.post(
    '/api/notifications/push/subscribe/',
    serializeSubscription(subscription, languageCode),
  );
  return response.data as BrowserPushSubscribeResponse;
}

export async function unsubscribeFromWebPush(): Promise<BrowserPushStatus> {
  if (!isBrowserPushApiAvailable()) {
    throw new Error('web_push_unsupported');
  }

  const registration = await navigator.serviceWorker.getRegistration(WEB_PUSH_SERVICE_WORKER_URL)
    || await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  const endpoint = subscription?.endpoint || '';

  if (subscription) {
    await subscription.unsubscribe();
  }

  const response = await api.post('/api/notifications/push/unsubscribe/', { endpoint });
  return response.data as BrowserPushStatus;
}
